import React from 'react';

/* ----------------------------------------------------------------------------
 * Analysis hero: BioAge centerpiece + Mortality Risk and Rate of Aging gauges.
 * Scales mirror the PDF report:
 *   - BioAge (PranaGen composite): fixed ±15y arc, younger = teal / older = navy
 *   - Mortality Risk: three equal bands (Low <5%, Moderate <15%, Elevated)
 *   - Rate of Aging: 0..2 dial shown as N.Nx (Bortz ÷ chronological)
 * -------------------------------------------------------------------------- */

// angle: degrees measured from the top (12 o'clock), clockwise positive.
const polar = (cx, cy, r, deg) => {
  const a = (deg * Math.PI) / 180;
  return { x: cx + r * Math.sin(a), y: cy - r * Math.cos(a) };
};
const arcPath = (cx, cy, r, a0, a1) => {
  const s = polar(cx, cy, r, a0);
  const e = polar(cx, cy, r, a1);
  const large = a1 - a0 > 180 ? 1 : 0;
  return `M ${s.x.toFixed(2)} ${s.y.toFixed(2)} A ${r} ${r} 0 ${large} 1 ${e.x.toFixed(2)} ${e.y.toFixed(2)}`;
};

/* ---------------- BioAge semicircle (centerpiece) ---------------- */
const BioGauge = ({ chrono, pranagen }) => {
  const W = 280, H = 216, cx = 140, cy = 140, r = 96, sw = 20;
  const delta = (pranagen ?? chrono) - chrono;
  const frac = Math.max(-1, Math.min(1, delta / 15));
  const ang = frac * 120;
  const tip = polar(cx, cy, r - 8, ang);
  const lend = polar(cx, cy, r, -120);
  const rend = polar(cx, cy, r, 120);
  const t0i = polar(cx, cy, r - sw / 2 - 2, 0);
  const t0o = polar(cx, cy, r + sw / 2 + 2, 0);
  return (
    <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`}>
      <defs>
        <linearGradient id="ah-ytk" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor="#2E97A8" />
          <stop offset="1" stopColor="#7FC4CC" />
        </linearGradient>
        <linearGradient id="ah-nvy" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor="#1C5568" />
          <stop offset="1" stopColor="#0F3D4E" />
        </linearGradient>
        <filter id="ah-ns" x="-40%" y="-40%" width="180%" height="180%">
          <feDropShadow dx="0" dy="1.5" stdDeviation="2" floodColor="#0F3D4E" floodOpacity="0.35" />
        </filter>
      </defs>
      <path d={arcPath(cx, cy, r, -120, -2)} fill="none" stroke="url(#ah-ytk)" strokeWidth={sw} strokeLinecap="round" />
      <path d={arcPath(cx, cy, r, 2, 120)} fill="none" stroke="url(#ah-nvy)" strokeWidth={sw} strokeLinecap="round" />
      <text x={lend.x - 2} y={lend.y + 18} textAnchor="middle" dominantBaseline="central" fontFamily="Manrope, sans-serif" fontSize="12" fontWeight="600" fill="#4A555B">-15</text>
      <text x={rend.x + 2} y={rend.y + 18} textAnchor="middle" dominantBaseline="central" fontFamily="Manrope, sans-serif" fontSize="12" fontWeight="600" fill="#4A555B">+15</text>
      <text x={cx} y={cy - r - 13} textAnchor="middle" dominantBaseline="central" fontFamily="Manrope, sans-serif" fontSize="12" fontWeight="600" fill="#4A555B">0</text>
      <line x1={t0i.x} y1={t0i.y} x2={t0o.x} y2={t0o.y} stroke="#1F2A30" strokeWidth="2.5" strokeLinecap="round" />
      <line x1={cx} y1={cy} x2={tip.x} y2={tip.y} stroke="#0F3D4E" strokeWidth="5" strokeLinecap="round" filter="url(#ah-ns)" />
      <line x1={cx} y1={cy} x2={tip.x} y2={tip.y} stroke="#fff" strokeWidth="2" strokeLinecap="round" />
      <circle cx={cx} cy={cy} r="8" fill="#0F3D4E" filter="url(#ah-ns)" />
      <circle cx={cx} cy={cy} r="3.5" fill="#fff" />
    </svg>
  );
};

/* ---------------- Mortality donut ---------------- */
const MortalityGauge = ({ m }) => {
  const W = 168, H = 168, cx = 84, cy = 84, r = 62, sw = 15, g = 6;
  const bands = [['#7FC4CC', 0], ['#3FA8B8', 1], ['#0F3D4E', 2]];
  let pos;
  if (m < 5) pos = (m / 5) / 3;
  else if (m < 15) pos = (1 + (m - 5) / 10) / 3;
  else pos = (2 + Math.min((m - 15) / 25, 1)) / 3;
  const ang = pos * 360;
  const bi = polar(cx, cy, r - sw / 2 - 1, ang);
  const bo = polar(cx, cy, r + sw / 2 + 1, ang);
  return (
    <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`}>
      {bands.map(([col, i]) => (
        <path key={i} d={arcPath(cx, cy, r, i * 120 + g / 2, (i + 1) * 120 - g / 2)} fill="none" stroke={col} strokeWidth={sw} strokeLinecap="round" />
      ))}
      <line x1={bi.x} y1={bi.y} x2={bo.x} y2={bo.y} stroke="#0F3D4E" strokeWidth="6" strokeLinecap="round" />
      <line x1={bi.x} y1={bi.y} x2={bo.x} y2={bo.y} stroke="#fff" strokeWidth="2.5" strokeLinecap="round" />
      <text x={cx} y={cy - 2} textAnchor="middle" fontFamily="Manrope, sans-serif" fontSize="27" fontWeight="800" fill="#1F2A30">{m}%</text>
    </svg>
  );
};

/* ---------------- Rate of aging dial (no 0/2 labels) ---------------- */
const PaceGauge = ({ rate }) => {
  const W = 168, H = 168, cx = 84, cy = 84, r = 62, sw = 15;
  const ang = Math.max(-165, Math.min(165, 165 * (rate - 1)));
  const bi = polar(cx, cy, r - sw / 2 - 1, ang);
  const bo = polar(cx, cy, r + sw / 2 + 1, ang);
  return (
    <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`}>
      <path d={arcPath(cx, cy, r, -165, -4)} fill="none" stroke="#7FC4CC" strokeWidth={sw} strokeLinecap="round" />
      <path d={arcPath(cx, cy, r, 4, 165)} fill="none" stroke="#0F3D4E" strokeWidth={sw} strokeLinecap="round" />
      <line x1={bi.x} y1={bi.y} x2={bo.x} y2={bo.y} stroke="#0F3D4E" strokeWidth="6" strokeLinecap="round" />
      <line x1={bi.x} y1={bi.y} x2={bo.x} y2={bo.y} stroke="#fff" strokeWidth="2.5" strokeLinecap="round" />
      <text x={cx} y={cy + 2} textAnchor="middle" fontFamily="Manrope, sans-serif" fontSize="25" fontWeight="800" fill="#1F2A30">{rate.toFixed(1)}x</text>
    </svg>
  );
};

export const AnalysisHero = ({ analysis }) => {
  const chrono = analysis.chronological_age;
  const pranagen = analysis.pranagen_clock ?? analysis.pheno_age;
  const pheno = analysis.pheno_age;
  const bortz = analysis.bortz_age;
  const mortality = analysis.mortality_risk;

  const hasBio = pranagen != null && chrono != null;
  const hasPace = bortz != null && chrono;
  const hasMort = mortality != null && mortality !== undefined;

  const delta = hasBio ? +(pranagen - chrono).toFixed(1) : null;
  const younger = delta != null && delta < 0;
  const onTrack = delta != null && Math.abs(delta) < 2;
  const rate = hasPace ? +(bortz / chrono).toFixed(2) : null;

  // delta pill
  let dColor = '#196a70', dBg = 'rgba(0,96,169,.10)', dText = '—';
  if (delta != null) {
    if (onTrack) { dColor = '#196a70'; dBg = 'rgba(0,96,169,.10)'; dText = `On track · ${delta > 0 ? '+' : ''}${delta} yrs`; }
    else if (younger) { dColor = '#0F3D4E'; dBg = 'rgba(63,168,184,.16)'; dText = `${Math.abs(delta)} yrs younger`; }
    else { dColor = '#0F3D4E'; dBg = 'rgba(15,61,78,.10)'; dText = `+${delta} yrs older`; }
  }

  // mortality label
  let mLbl = '', mCol = '#227C8B', mBg = 'rgba(63,168,184,.16)';
  if (hasMort) {
    if (mortality < 5) { mLbl = 'Low Risk'; mCol = '#2E97A8'; mBg = 'rgba(127,196,204,.22)'; }
    else if (mortality < 15) { mLbl = 'Moderate Risk'; mCol = '#227C8B'; mBg = 'rgba(63,168,184,.16)'; }
    else { mLbl = 'Elevated Risk'; mCol = '#0F3D4E'; mBg = 'rgba(15,61,78,.10)'; }
  }

  const paceFaster = rate != null && rate > 1.03;
  const paceSlower = rate != null && rate < 0.97;
  const paceTxt = paceSlower ? 'Slower than average' : paceFaster ? 'Faster than average' : 'Average pace';

  return (
    <div className="bg-white border border-slate-200 rounded-2xl shadow-soft overflow-hidden">
      <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
        <h2 className="font-heading text-lg font-bold text-slate-900">Biological Age Overview</h2>
        <span className="text-xs font-medium text-slate-400">Composite of PhenoAge &amp; Bortz Age</span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-[1fr_1.5fr_1fr] items-center gap-6 md:gap-3 px-6 py-8">
        {/* Mortality Risk */}
        <div className="flex flex-col items-center text-center order-2 md:order-1">
          <div className="text-[11.5px] font-bold tracking-[0.09em] uppercase text-slate-400 mb-1.5">10-Yr Mortality Risk</div>
          {hasMort ? (
            <>
              <MortalityGauge m={+mortality} />
              <span className="mt-1 text-[11.5px] font-bold px-2.5 py-1 rounded-full" style={{ color: mCol, background: mBg }}>{mLbl}</span>
            </>
          ) : (
            <div className="h-[168px] flex items-center text-sm text-slate-400">Not available</div>
          )}
        </div>

        {/* BioAge centerpiece */}
        <div className="flex flex-col items-center text-center order-1 md:order-2">
          <div className="text-xs font-bold tracking-[0.12em] uppercase text-[#196a70]">Biological Age</div>
          {hasBio ? (
            <>
              <BioGauge chrono={chrono} pranagen={pranagen} />
              <div className="text-[56px] leading-none font-extrabold text-[#0F3D4E] tracking-tight -mt-2">
                {pranagen.toFixed(1)}<span className="text-lg font-semibold text-slate-400 ml-1">yrs</span>
              </div>
              <span className="inline-flex items-center gap-1.5 mt-2.5 text-[13px] font-bold px-3.5 py-1.5 rounded-full" style={{ color: dColor, background: dBg }}>{dText}</span>
              <div className="text-[12.5px] text-slate-500 mt-2.5 font-medium">vs chronological <b className="text-slate-800">{chrono?.toFixed(1)}</b></div>
              <div className="flex gap-3 justify-center mt-2.5">
                <span className="flex items-center gap-1.5 text-[10.5px] font-bold tracking-wide text-slate-500"><span className="w-2.5 h-2.5 rounded-full" style={{ background: '#3FA8B8' }} />YOUNGER</span>
                <span className="flex items-center gap-1.5 text-[10.5px] font-bold tracking-wide text-slate-500"><span className="w-2.5 h-2.5 rounded-full" style={{ background: '#0F3D4E' }} />OLDER</span>
              </div>
            </>
          ) : (
            <div className="h-[220px] flex items-center text-sm text-slate-400">Recalculate to see biological age</div>
          )}
        </div>

        {/* Rate of Aging */}
        <div className="flex flex-col items-center text-center order-3">
          <div className="text-[11.5px] font-bold tracking-[0.09em] uppercase text-slate-400 mb-1.5">Rate of Aging</div>
          {hasPace ? (
            <>
              <PaceGauge rate={rate} />
              <span className="mt-1 text-[11.5px] font-bold px-2.5 py-1 rounded-full" style={{ color: paceFaster ? '#0F3D4E' : '#227C8B', background: paceFaster ? 'rgba(15,61,78,.10)' : 'rgba(63,168,184,.16)' }}>{paceTxt}</span>
            </>
          ) : (
            <div className="h-[168px] flex items-center text-sm text-slate-400">Not available</div>
          )}
        </div>
      </div>
    </div>
  );
};
